import { Component, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  BarChartWidgetData,
  GridsterWidgetItem,
  PieChartWidgetData,
  TableChartTab,
} from 'src/app/core/providers/dashboardWidget/dashboardWidget.models';
import { PieChartComponent } from './components/pie-chart/pie-chart.component';
import { BarChartComponent } from './components/bar-chart/bar-chart.component';
import { GridsterWidgetsComponent } from 'src/app/shared/gridster-widgets/gridster-widgets.component';
import { GridsterConfig, GridType, DisplayGrid } from 'angular-gridster2';
import { ApiService } from 'src/app/core/providers/api/api.service';
import { Subscription, forkJoin } from 'rxjs';
import { CompaniesStatistics } from 'src/app/core/providers/company/company.models';
import {
  TasksAnnualStatusByMonth,
  TasksAnnualStatusListResponse,
  TaskAnnualStatusItem,
  TasksTypeListResponse,
  TasksPriorityListResponse,
  TaskPriorityTypeItem,
} from 'src/app/core/providers/task/task.models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SearchFormComponent } from 'src/app/shared/search-form/search-form.component';
import { extractLastSegmentFromUrl, formatUserName } from 'src/app/core/helpers/utils';
import { TaskPlannedListResponse } from 'src/app/core/providers/taskPlanned/taskPlanned.models';
import { TableChartComponent } from './components/table-chart/table-chart.component';
import { CommentWidgetComponent } from './components/comment-widget/comment-widget.component';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TaskEditModalComponent } from '../planning/components/task-edit-modal/task-edit-modal.component';
import { TaskAssigneeModalComponent } from '../planning/components/task-assignee-modal/task-assignee-modal.component';
import { ConfirmationModalComponent } from 'src/app/shared/confirmation-modal/confirmation-modal.component';
import { MessageBusService } from 'src/app/core/providers/busMessage/messageBusService';
import { ToastService } from 'src/app/core/providers/toast/toast.service';
import { DurationModalComponent } from '../planning/components/duration-modal/duration-modal.component';
import { AuthenticationService } from 'src/app/core/providers/auth/auth.service';
import { TaskTimeDisplayService } from 'src/app/core/providers/task-time-display/task-time-display.service';

/** Ligne API `company_roles` (filtre dashboard / dossiers). */
interface CompanyRoleApiRow {
  id: string;
  code: string;
  name?: string;
  isUnique?: boolean;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    GridsterWidgetsComponent,
    TranslateModule,
    SearchFormComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  options: GridsterConfig = {
    gridType: GridType.VerticalFixed,
    displayGrid: DisplayGrid.None,
    draggable: { enabled: true },
    resizable: { enabled: true },
    pushItems: true,
    swap: true,
    minCols: 6,
    minRows: 6,
    maxCols: 6,
    fixedRowHeight: 280,
    itemChangeCallback: () => this.saveLayout(),
    itemResizeCallback: () => this.saveLayout(),
  };
  subscription: Subscription = new Subscription();
  widgets: Array<GridsterWidgetItem> = [];
  currentYear = new Date().getFullYear();
  formFields: any = [];
  searchValue: any = {};
  taskStatuses: any[] = []; // Ajouter cette ligne
  @ViewChild('folderTemplate', { static: true })
  folderTemplate!: TemplateRef<any>;

  @ViewChild('userTemplate', { static: true })
  userTemplate!: TemplateRef<any>;

  @ViewChild('companyIconTemplate', { static: true })
  companyIconTemplate!: TemplateRef<any>;

  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;

  /** IDs des rôles « uniques » (expert comptable, superviseur, responsable) — aligné sur my-folders. */
  private uniqueRoleIds: Record<string, string> = {};
  private uniqueRoleData: Record<string, { id: string; code: string }> = {};
  private teamUserIdsCache: Record<string, string[]> = {};
  private searchRequestToken = 0;

  constructor(
    public translate: TranslateService,
    public authService: AuthenticationService<unknown>,
    private apiService: ApiService,
    private router: Router,
    private modalService: NgbModal,
    private messageBus: MessageBusService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private taskTimeDisplay: TaskTimeDisplayService
  ) {
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let y = 2021; y <= currentYear; y++) {
      yearOptions.push({ value: y, label: String(y) });
    }
    this.formFields = [
      {
        name: 'site',
        label: 'Site',
        type: 'entity',
        apiEndpoint: 'sites',
        col: 3,
        multiple: true,
        hideValidateButton: true,
        optionValue: '@id',
        optionName: 'name',
        searchKey: 'name',
      },
      {
        name: 'team',
        label: 'Équipe',
        type: 'entity',
        apiEndpoint: 'teams',
        col: 3,
      },
      {
        name: 'assignedUser',
        label: 'Assigné',
        type: 'entity',
        apiEndpoint: 'user_firms',
        col: 3,
        multiple: false,
        optionValue: 'user',
        optionName: 'fullName',
        searchKey: 'user.fullName',
        dependsOn: ['team', 'site'],
        mapFilters: (deps: any) => {
          const filters: Record<string, any> = {};
          if (deps?.team) {
            filters['user.teamMemberships.team'] = deps.team;
          }
          if (Array.isArray(deps?.site) && deps.site.length > 0) {
            filters['sites'] = deps.site;
          }
          return filters;
        },
      },
      {
        name: 'year',
        label: 'Année',
        type: 'select',
        col: 3,
        options: yearOptions,
        defaultValue: this.currentYear,
        required: true,
        clearable: false,
      },
      {
        name: 'companyStatusCodes',
        label: 'Statut dossier',
        type: 'entity',
        apiEndpoint: 'company_statuses',
        apiFilters: { 'order[filter]': 'ASC' },
        col: 3,
        multiple: true,
        optionValue: 'code',
        optionName: 'name',
        searchKey: 'name',
        defaultValue: ['CLIENT'],
      },
    ];
  }

  onAssigneeClick(event: { collaborator: any; companyId?: string }): void {
    const user = event?.collaborator;
    if (!user) return;
    import('../settings/users/components/user-firm-modal/user-firm-modal.component').then(m => {
      const modalRef = this.modalService.open(m.UserFirmModalComponent, { centered: true, size: 'lg' });
      modalRef.componentInstance.userId = user.userId || user.id;
      modalRef.componentInstance.mode = 'show';
      modalRef.componentInstance.isCollapsed = true;
      modalRef.componentInstance.hideEditButton = true;
      modalRef.componentInstance.updateUserForm = {
        personalInfo: {
          civility: '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: ''
        },
        profileInfo: {
          phone: user.phone || '',
          hourlyRate: null,
          categories: [],
          dailyAvailability: '',
          weeklyAvailability: '',
          availabilities: [],
          profile: ''
        }
      } as any;
    });
  }
  ngOnInit() {
    const defaultFormValue = {
      year: this.currentYear,
      companyStatusCodes: ['CLIENT'],
    };
    this.searchValue = defaultFormValue;
    const { filtre, companiesFiltre, plannedFiltre } =
      this.buildDashboardFilters(defaultFormValue, []);

    this.createCompaniesWidget(companiesFiltre);
    this.createTaskPlannedTabsWidget(plannedFiltre);
    this.createTaskWidgets(filtre);
    this.ensureCommentWidget();
    this.loadUniqueRoles();
    this.fetchTaskStatuses();
  }

  /** Charge les rôles `isUnique` et insère les filtres Expert comptable / Superviseur / Responsable (comme my-folders). */
  private loadUniqueRoles(): void {
    this.subscription.add(
      this.apiService
        .getAll('company_roles', 1, 100, { type: 'USER', 'order[filter]': 'ASC' })
        .subscribe({
          next: (response: { member?: CompanyRoleApiRow[] }) => {
            const member = response.member;
            if (!Array.isArray(member)) {
              return;
            }
            const uniqueRoles = member.filter((role) => role.isUnique === true);

            const expertComptable = uniqueRoles.find(
              (role) =>
                role.name?.toLowerCase().includes('expert') ||
                role.name?.toLowerCase().includes('comptable') ||
                role.code?.toUpperCase().includes('EXPERT') ||
                role.code?.toUpperCase().includes('ACCOUNTANT') ||
                role.code?.toUpperCase() === 'EXPERT_COMPTABLE'
            );
            const superviseur = uniqueRoles.find(
              (role) =>
                role.name?.toLowerCase().includes('superviseur') ||
                role.code?.toUpperCase() === 'SUPERVISOR' ||
                role.code?.toUpperCase() === 'SUPERVISEUR'
            );
            const responsable = uniqueRoles.find(
              (role) =>
                role.name?.toLowerCase().includes('responsable') ||
                role.code?.toUpperCase() === 'RESPONSABLE' ||
                role.code?.toUpperCase() === 'MANAGER'
            );

            if (expertComptable) {
              const roleId =
                typeof expertComptable.id === 'string' &&
                expertComptable.id.includes('/')
                  ? extractLastSegmentFromUrl(expertComptable.id)
                  : expertComptable.id;
              this.uniqueRoleIds['expertComptable'] = roleId;
              this.uniqueRoleData['expertComptable'] = {
                id: roleId,
                code: expertComptable.code,
              };
            }
            if (superviseur) {
              const roleId =
                typeof superviseur.id === 'string' && superviseur.id.includes('/')
                  ? extractLastSegmentFromUrl(superviseur.id)
                  : superviseur.id;
              this.uniqueRoleIds['superviseur'] = roleId;
              this.uniqueRoleData['superviseur'] = {
                id: roleId,
                code: superviseur.code,
              };
            }
            if (responsable) {
              const roleId =
                typeof responsable.id === 'string' && responsable.id.includes('/')
                  ? extractLastSegmentFromUrl(responsable.id)
                  : responsable.id;
              this.uniqueRoleIds['responsable'] = roleId;
              this.uniqueRoleData['responsable'] = {
                id: roleId,
                code: responsable.code,
              };
            }

            this.updateFormFieldsWithUniqueRoles();
          },
        })
    );
  }

  /** Insère les filtres rôle unique après les filtres d'équipe. */
  private updateFormFieldsWithUniqueRoles(): void {
    const insertAfterIndex = this.formFields.findIndex((field: { name?: string }) => field.name === 'team');
    if (insertAfterIndex === -1) {
      return;
    }

    const uniqueRoleFilters: Record<string, unknown>[] = [];
    if (this.uniqueRoleData['expertComptable']) {
      uniqueRoleFilters.push({
        name: 'expertComptable',
        label: 'Expert comptable',
        type: 'entity',
        apiEndpoint: 'user_firms',
        col: 3,
        multiple: false,
        optionValue: 'user',
        optionName: 'fullName',
        searchKey: 'user.fullName',
        apiFilters: {
          'profile.code': 'PROFILE_CPA',
        },
        dependsOn: 'site',
        mapFilters: (siteIds: any) =>
          Array.isArray(siteIds) && siteIds.length > 0
            ? { 'profile.code': 'PROFILE_CPA', sites: siteIds }
            : { 'profile.code': 'PROFILE_CPA' },
      });
    }
    if (this.uniqueRoleData['superviseur']) {
      uniqueRoleFilters.push({
        name: 'superviseur',
        label: 'Superviseur',
        type: 'entity',
        apiEndpoint: 'user_firms',
        col: 3,
        multiple: false,
        optionValue: 'user',
        optionName: 'fullName',
        searchKey: 'user.fullName',
        apiFilters: {
          'role.code': this.uniqueRoleData['superviseur'].code,
        },
        dependsOn: 'site',
        mapFilters: (siteIds: any) =>
          Array.isArray(siteIds) && siteIds.length > 0
            ? { 'role.code': this.uniqueRoleData['superviseur'].code, sites: siteIds }
            : { 'role.code': this.uniqueRoleData['superviseur'].code },
      });
    }
    if (this.uniqueRoleData['responsable']) {
      uniqueRoleFilters.push({
        name: 'responsable',
        label: 'Responsable',
        type: 'entity',
        apiEndpoint: 'user_firms',
        col: 3,
        multiple: true,
        hideValidateButton: true,
        /** Aligné sur le filtre « Assigné » du planning (`executor` : userId + utilisateur courant). */
        optionValue: 'userId',
        optionName: 'fullName',
        searchKey: 'user.fullName',
        defaultValue: this.authService.currentUserValue?.id ?? undefined,
        dependsOn: ['team', 'site'],
        mapFilters: (deps: any) => {
          const filters: Record<string, any> = {
            'role.code': this.uniqueRoleData['responsable'].code,
          };
          if (deps?.team) {
            filters['user.teamMemberships.team'] = deps.team;
          }
          if (Array.isArray(deps?.site) && deps.site.length > 0) {
            filters['sites'] = deps.site;
          }
          return filters;
        },
        apiFilters: {
          'role.code': this.uniqueRoleData['responsable'].code,
        },
      });
    }

    this.formFields = [
      ...this.formFields.slice(0, insertAfterIndex + 1),
      ...uniqueRoleFilters,
      ...this.formFields.slice(insertAfterIndex + 1),
    ];
    this.cdr.markForCheck();
  }



  // Ouvre le modal de durée depuis une tâche planifiée (dashboard widget)
  openDurationModalFromPlanned(item: any): void {
    if (!item || !item.task) return;

    // Récupérer l'onglet actuellement sélectionné avant la validation
    const currentWidget = this.widgets.find(w => w.id === 'task-planned-tabs');
    const currentTabIndex = currentWidget?.data?.selectedTabIndex || 0;

    // S'assurer d'utiliser la dernière version de la tâche depuis le widget
    // pour éviter d'utiliser une référence obsolète
    let taskToUse = item.task;
    if (currentWidget?.data?.tabs?.[currentTabIndex]?.data) {
      const currentTab = currentWidget.data.tabs[currentTabIndex];
      const taskItem = currentTab.data.find((taskItem: any) =>
        taskItem.id === item.id || taskItem.task?.id === item.task?.id
      );
      if (taskItem && taskItem.task) {
        taskToUse = taskItem.task;
      }
    }

    const modalRef = this.modalService.open(DurationModalComponent, {
      centered: true,
      size: 'sm',
    });
    modalRef.componentInstance.task = taskToUse;
    modalRef.componentInstance.planningTask = item; // si le composant l'utilise
    this.subscription.add(
      modalRef.componentInstance.sucessTaskTimeCreation?.subscribe((data: any) => {
        // Si l'événement contient un changement de statut explicite, l'utiliser
        if (data && data.action === 'changeStatus' && data.status) {
          this.updateTaskStatusLocally(item, currentTabIndex, data.status);
        } else if (data && data.status) {
          // Si la réponse contient le statut (ajouté par le modal), l'utiliser
          this.updateTaskStatusLocally(item, currentTabIndex, data.status);
        } else if (data && data.task && data.task.status) {
          // Si la réponse contient la tâche mise à jour avec son statut, l'utiliser
          this.updateTaskStatusLocally(item, currentTabIndex, data.task.status.code);
        }
        // Si aucune information de statut n'est disponible, on ne met pas à jour
        // L'utilisateur devra rafraîchir la page pour voir le statut réel
      })
    );
  }

  // Met à jour localement le statut d'une tâche dans le widget
  private updateTaskStatusLocally(item: any, currentTabIndex: number, newStatus: string = 'DONE'): void {
    const currentWidget = this.widgets.find(w => w.id === 'task-planned-tabs');
    if (!currentWidget || !currentWidget.data?.tabs) return;

    const currentTab = currentWidget.data.tabs[currentTabIndex];
    if (!currentTab || !currentTab.data) return;

    // Trouver la tâche dans l'onglet actuel
    const taskIndex = currentTab.data.findIndex((taskItem: any) =>
      taskItem.id === item.id || taskItem.task?.id === item.task?.id
    );

    if (taskIndex !== -1) {
      // Mettre à jour le statut de la tâche avec le statut réel
      if (currentTab.data[taskIndex].task) {
        currentTab.data[taskIndex].task = {
          ...currentTab.data[taskIndex].task,
          status: { code: newStatus }
        };
      }
      // Mettre à jour isTaskDone uniquement si le statut est DONE
      currentTab.data[taskIndex].isTaskDone = newStatus === 'DONE';

      // Créer de nouvelles références pour forcer la détection des changements
      currentTab.data = [...currentTab.data];
      currentWidget.data.tabs = [...currentWidget.data.tabs];
      currentWidget.data = { ...currentWidget.data };

      // Forcer la détection des changements
      this.cdr.detectChanges();
    }
  }

  // --- Statistiques sociétés ---
  createCompaniesWidget(filtre: any = {}) {
    const legalFormRequest = this.apiService.getAll('companies/statistics', 1, 100, {
      field: 'legalForm',
      ...filtre,
    });

    this.subscription.add(
      forkJoin({
        legalForm: legalFormRequest,
      }).subscribe({
        next: ({ legalForm }) => {
          const pieChartData = this.buildCompaniesPieChartData(legalForm);
          this.addWidget({
            id: 'companies-pie',
            x: 0,
            y: 0,
            cols: 2,
            rows: 1,
            component: PieChartComponent,
            data: pieChartData,
          });
        },
      })
    );
  }

  buildCompaniesPieChartData(
    legalFormResponse: CompaniesStatistics
  ): PieChartWidgetData {
    return {
      title: `Portefeuille et Répartition`,
      values: legalFormResponse.member.map((item) => item.count),
      labels: legalFormResponse.member.map(
        (item) =>
          item.legalForm_name ?? this.translate.instant('dashboard.noLegalForm')
      ),
      backgroundColor: ['#0d6efd'],
    };
  }

  createTaskPlannedTabsWidget(filtre: any = {}, selectedTabIndex: number = 1): void {
    // Utiliser un Map pour garantir l'ordre : 0 = Tâches en retard, 1 = Tâches du jour, 2 = Tâches à venir
    const tabsMap = new Map<string, TableChartTab>();
    const columnTemplate = [
      {
        field: 'companyIcon',
        header: '',
        cellTemplate: this.companyIconTemplate,
      },
      { field: 'taskName', header: 'Titre de la tâche' },
      {
        field: 'user',
        header: 'Assigné à',
        cellTemplate: this.userTemplate
      },
      {
        field: 'company',
        header: 'Société',
        cellTemplate: this.folderTemplate,
      },
      {
        field: 'actions',
        header: 'Action',
        cellTemplate: this.actionTemplate,
      },
    ];

    const checkAndAddWidget = () => {
      if (tabsMap.size === 3) {
        // Réorganiser les onglets dans l'ordre voulu : retard, jour, venir
        const orderedTabs: TableChartTab[] = [
          tabsMap.get('retard')!,
          tabsMap.get('jour')!,
          tabsMap.get('venir')!,
        ];
        this.addTaskPlannedTabsWidget(orderedTabs, selectedTabIndex);
      }
    };

    // Onglet "Tâches en retard" (index 0 - à gauche)
    this.apiService
      .getAll('task_planneds', 1, 100, {
        'task.priorityType': 'URGENT',
        'exists[user]': 'true',
        ...filtre,
      })
      .subscribe({
        next: (response: TaskPlannedListResponse) => {
          tabsMap.set('retard', {
            label: 'Tâches en retard',
            columns: columnTemplate,
            data: response.member.map((item) => ({
              id: item.id,
              taskName: item.task ? item.task.name : '',
              user: item.user
                ? this.formatUserName(item.user.firstName, item.user.lastName)
                : '',
              assigneeUser: item.user || null,
              company: item.task.company ? item.task.company : null,
              task: item.task,
              openDurationModal: () => this.openDurationModalFromPlanned(item),
              isTaskDone: this.isTaskDone(item.task),
            })),
            totalItems: response.totalItems,
          });
          checkAndAddWidget();
        },
      });

    // Onglet "Tâches du jour" (index 1 - au milieu et par défaut)
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    this.apiService
      .getAll('task_planneds', 1, 100, {
        'startTime[after]': startOfDay.toISOString(),
        'startTime[before]': endOfDay.toISOString(),
        'exists[user]': 'true',
        ...filtre,
      })
      .subscribe({
        next: (response: TaskPlannedListResponse) => {
          tabsMap.set('jour', {
            label: 'Tâches du jour',
            columns: columnTemplate,
            data: response.member.map((item) => ({
              id: item.id,
              taskName: item.task ? item.task.name : '',
              user: item.user
                  ? this.formatUserName(item.user.firstName, item.user.lastName)
                  : '',
              assigneeUser: item.user || null,
              company: item.task.company ? item.task.company : null,
              task: item.task,
              openDurationModal: () => this.openDurationModalFromPlanned(item),
              isTaskDone: this.isTaskDone(item.task),
            })),
            totalItems: response.totalItems,
          });
          checkAndAddWidget();
        },
      });

    // Onglet "Tâches à venir" (index 2 - à droite)
    this.apiService
      .getAll('task_planneds', 1, 100, {
        'startTime[after]': new Date(
          Date.now() + 5 * 24 * 60 * 60 * 1000
        ).toISOString(), // J+5
        'exists[user]': 'true',
        ...filtre,
      })
      .subscribe({
        next: (response: TaskPlannedListResponse) => {
          tabsMap.set('venir', {
            label: 'Tâches à venir',
            columns: columnTemplate,
            data: response.member.map((item) => ({
              id: item.id,
              taskName: item.task ? item.task.name : '',
              user: item.user
                  ? this.formatUserName(item.user.firstName, item.user.lastName)
                  : '',
              assigneeUser: item.user || null,
              company: item.task.company ? item.task.company : null,
              task: item.task,
              openDurationModal: () => this.openDurationModalFromPlanned(item),
              isTaskDone: this.isTaskDone(item.task),
            })),
            totalItems: response.totalItems,
          });
          checkAndAddWidget();
        },
      });
  }

  addTaskPlannedTabsWidget(tabs: TableChartTab[], selectedTabIndex: number = 1) {
    this.addWidget({
      id: 'task-planned-tabs',
      x: 0,
      y: 1,
      cols: 3,
      rows: 1,
      component: TableChartComponent,
      data: {
        title: 'Tâches',
        tabs,
        selectedTabIndex: selectedTabIndex,
      },
    });
  }

  createCommentWidget(): void {
    this.addWidget({
      id: 'comment-widget',
      x: 3,
      y: 2,
      cols: 3,
      rows: 1,
      component: CommentWidgetComponent,
      data: {
        title: 'Commentaires',
      },
    });
  }

  /** Réinjecte le widget après chaque reconstruction du dashboard (recherche / reload). */
  private ensureCommentWidget(): void {
    queueMicrotask(() => this.createCommentWidget());
  }

  createTaskWidgets(filtre: any = {}): void {
    if (!filtre.dueYear) {
      filtre.dueYear = this.getCurrentYear();
    }
    const currentMonth = this.getCurrentMonth();

    this.createTasksAnnualStatusWidget(
      'Annual-Status',
      'Vue annuel',
      { x: 2, y: 0, cols: 4, rows: 1 },
      {
        field: 'dueMonth,isDone',
        type: 'OBLIGATION',
        'order[dueMonth]': 'ASC',
        groupBy: 'dueMonth',
        ...filtre,
      }
    );

    this.createDueDateWidget(
      'Due-Date',
      'Mes échéances',
      { x: 3, y: 1, cols: 3, rows: 1 },
      {
        field: 'taskBatch.parent,isDone',
        type: 'OBLIGATION',
        'order[taskBatch.parent]': 'ASC',
        groupBy: 'taskBatch.parent',
        dueMonth: [currentMonth],
        ...filtre,
      }
    );

    this.createTasksPrioritiesWidget(
      'Priorities',
      'Priorités',
      { x: 0, y: 2, cols: 2, rows: 1 },
      {
        field: 'priorityType',
        type: 'OBLIGATION',
        dueMonth: [currentMonth],
        ...filtre,
      }
    );
  }

  getCurrentMonth(): number {
    return new Date().getMonth() + 1; // getMonth() retourne 0-11, donc +1
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  fetchTaskStatuses(): void {
    this.subscription.add(
      this.apiService.getAll('task_statuses', 1, 100, { 'order[filter]': 'ASC' }).subscribe({
        next: (response: any) => {
          this.taskStatuses = response.member || [];
        },
        error: (error) => {
          console.error('Erreur lors de la récupération des statuts:', error);
        }
      })
    );
  }

  // --- Statistiques tâches ---
  createDueDateWidget(
    widgetId: string,
    title: string,
    position: { x: number; y: number; cols: number; rows: number },
    filtre: any = {}
  ) {
    this.subscription.add(
      this.apiService.getAll('tasks/statistics', 1, 100, filtre).subscribe({
        next: (response: TasksTypeListResponse) => {
          const barChartData = this.buildDueDateBarChartData(response);
          this.addWidget({
            id: widgetId,
            x: position.x,
            y: position.y,
            cols: position.cols,
            rows: position.rows,
            component: BarChartComponent,
            data: {
              ...barChartData,
              title,
              filterConfig: {
                availableMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                selectedMonths: filtre.dueMonth,
                onMonthsChanged: (months: number[]) =>
                  this.onChangeDueDateMonth(widgetId, months),
                activateCategory: true,
                selectedCategoryCodes: filtre.categories,
                onCategoryChanged: (categoryCodes: string[]) =>
                  this.onChangeDueDateCategory(widgetId, categoryCodes),
                filtre,              },
              onBarClick: (event: any) => this.onBarClick(event),
            },
          });
        },
      })
    );
  }

  buildDueDateBarChartData(
    response: TasksTypeListResponse
  ): BarChartWidgetData {
    // Fusionne tous les objets du tableau member en un seul objet
    const mergedMember = Object.assign({}, ...(response.member as any[]));

    // 1. Récupère tous les batchs (colonnes X)
    const batchIds = Object.keys(mergedMember);
    const batchNames = batchIds.map(
      (id: string) => mergedMember[id][0]?.taskBatch_parent_name ?? id
    );

    // 2. Récupère tous les statuts distincts (légendes Y)
    const statusSet = new Set<string>();
    batchIds.forEach((id: string) => {
      mergedMember[id].forEach((item: any) => {
        statusSet.add(item.isDone ? 'Fait' : 'Non fait');
      });
    });
    const statusNames = Array.from(statusSet);

    // 3. Construit un dataset par statut
    const datasets = statusNames.map((status) => {
      const data = batchIds.map((id) => {
        const found = mergedMember[id].find(
          (item: any) => (item.isDone ? 'Fait' : 'Non fait') === status
        );
        return found ? found.count : 0;
      });
      return {
        label: status,
        data,
        backgroundColor: this.getStatusColor(status),
      };
    });

    // 4 : tableau indexé comme labels, chaque entrée = {status: [TaskTypeItem]}
    const extraInfo: Array<Record<string, any[]>> = batchIds.map((batchId) => {
      const info: Record<string, any[]> = {};
      statusNames.forEach((status) => {
        info[status] = mergedMember[batchId].filter(
          (item: any) => (item.isDone ? 'Fait' : 'Non fait') === status
        );
      });
      return info;
    });

    return {
      labels: batchNames,
      datasets,
      extraInfo,
    };
  }

  /*
  onBarClick(event: any, widgetId: string) {
    const extraInfo = event.extraInfo;
    console.log('Bar clicked:', extraInfo);
    console.log('Widget ID:', widgetId);
    console.log('filtre => ', event.filter);

    if (widgetId === 'Annual-Status') {
      let statusIds = '';
      extraInfo.forEach((info: TaskAnnualStatusItem) => {
        statusIds += info.status_id + ',';
      });
      const queryParams = {
        'task.status': statusIds.slice(0, -1),
        'task.dueMonth': extraInfo.dueMonth,
      };

      this.router.navigate(['/deadlines'], {
        queryParams,
      });
    }
  }*/


  onBarClick(event: any): void {
    console.log('Bar clicked:', event);
    console.log('Event filter:', event.filter);
    console.log('Event extraInfo:', event.extraInfo);
    const queryParams: any = {};

    // Récupération des informations de filtre depuis searchValue
    if (this.searchValue && this.searchValue.year) {
      queryParams['task.dueYear'] = this.searchValue.year;
    } else {
      // Valeur par défaut : année courante
      queryParams['task.dueYear'] = this.currentYear.toString();
    }

    // Récupérer les informations du filtre si disponibles
    if (event.filter) {
      if (event.filter.dueMonth) {
        queryParams['task.dueMonth'] = event.filter.dueMonth;
      }
    }

    // Vérifier si c'est un clic sur "Non fait" en analysant les extraInfo
    if (event.extraInfo && event.extraInfo.length > 0) {
      // Vérifier si toutes les tâches sont "Non fait" (isDone = false)
      const allNonDone = event.extraInfo.every((info: any) => info.isDone === false);

      if (allNonDone) {
        // Sélectionner automatiquement tous les statuts sauf "Fait" en utilisant les IDs
        const nonDoneStatusNames = ['En attente', 'En cours', 'À superviser', 'Supervisé'];
        const nonDoneStatusIds = this.getStatusIdsByNames(nonDoneStatusNames);

        if (nonDoneStatusIds.length > 0) {
          // CORRECTION : Envoyer comme tableau au lieu de chaîne
          queryParams['task.status'] = nonDoneStatusIds;
        }
        queryParams['task.isDone'] = 'false';
      } else {
        // Logique existante pour les autres cas
        const statusIds = event.extraInfo.map((info: any) => info.status_id).filter(Boolean);
        if (statusIds.length > 0) {
          queryParams['task.status'] = statusIds;
        }
      }
    }

    console.log('queryParams to send:', queryParams);

    // Redirection
    this.router.navigate(['/deadlines'], {
      queryParams,
    });
  }

  private getStatusIdsByNames(statusNames: string[]): string[] {
    return statusNames.map(name => {
      const status = this.taskStatuses.find(s => s.name === name);
      return status ? status.id : null;
    }).filter(Boolean);
  }



  // --- Statistiques tâches ---
  createTasksAnnualStatusWidget(
    widgetId: string,
    title: string,
    position: { x: number; y: number; cols: number; rows: number },
    filtre: any = {}
  ) {
    this.subscription.add(
      this.apiService.getAll('tasks/statistics', 1, 100, filtre).subscribe({
        next: (response: TasksAnnualStatusListResponse) => {
          const barChartData =
            this.buildTasksAnnualStatusBarChartData(response);
          this.addWidget({
            id: widgetId,
            x: position.x,
            y: position.y,
            cols: position.cols,
            rows: position.rows,
            component: BarChartComponent,
            data: {
              ...barChartData,
              title,
              filterConfig: {
                filtre,
              },
              onBarClick: (event: any) => this.onBarClick(event),
            },
          });
        },
      })
    );
  }

  buildTasksAnnualStatusBarChartData(
  response: TasksAnnualStatusListResponse
): BarChartWidgetData {
  // 1. Récupère tous les mois présents
  const monthStatsByYear = response.member[0]; // maintenant c'est un seul objet
  const months = Object.keys(monthStatsByYear).sort((a, b) => +a - +b);
  const monthLabels = months.map((m) => this.getMonthName(m));

  // 2. Prépare les jeux de données : 'Fait' et 'Non fait'
  const statusMap = new Map<string, { name: string; data: number[] }>();
  statusMap.set('done', { name: 'Fait', data: Array(months.length).fill(0) });
  statusMap.set('not_done', {
    name: 'Non fait',
    data: Array(months.length).fill(0),
  });

  // Pour extraInfo : tableau indexé comme labels, chaque entrée = {status: [TaskAnnualStatusItem]}
  const extraInfo: Array<Record<string, TaskAnnualStatusItem[]>> = months.map(
    (month, index) => {
      const monthData = monthStatsByYear[month] ?? [];
      const info: Record<string, TaskAnnualStatusItem[]> = {};

      monthData.forEach((stat) => {
        const statusKey = stat.isDone ? 'done' : 'not_done';
        const statusName = stat.isDone ? 'Fait' : 'Non fait';

        // Ajoute la donnée au bon dataset
        statusMap.get(statusKey)!.data[index] = stat.count;

        // Stocke extra info
        if (!info[statusName]) info[statusName] = [];
        info[statusName].push(stat);
      });

      return info;
    }
  );

  // 3. Construit les datasets avec le nom et la couleur du statut
  const datasets = Array.from(statusMap.entries()).map(
    ([_, { name, data }]) => ({
      label: name,
      data,
      backgroundColor: this.getStatusColor(name),
    })
  );

  return {
    labels: monthLabels,
    datasets,
    extraInfo,
  };
}


  private getMonthName(monthNumber: string | number): string {
    const num =
      typeof monthNumber === 'string' ? parseInt(monthNumber, 10) : monthNumber;
    return this.translate.instant('MONTHS.' + num);
  }

  // --- Couleurs ---
  getStatusColor(statusIdOrName: string): string {
    // Utilise le nom ou l'id pour déterminer la couleur par défaut
    switch (statusIdOrName) {
      case 'WAITING':
      case 'En attente':
        return '#0d6efd'; // Bleu
      case 'IN_PROGRESS':
      case 'En cours':
        return '#ffc107'; // Jaune
      case 'TO_SUPERVISE':
      case 'À superviser':
        return '#6610f2'; // Violet
      case 'SUPERVISED':
      case 'Supervisée':
        return '#20c997'; // Vert turquoise
      case 'DONE':
      case 'Terminée':
      case 'Fait':
        return '#00CC75'; // Vert
      default:
        return '#C1C1C1'; // Gris par défaut
    }
  }

  // --- Widgets ---
  addWidget(widget: GridsterWidgetItem) {
    // Récupère la position sauvegardée si elle existe
    const saved = localStorage.getItem('dashboardWidgetsLayout');
    if (saved) {
      const savedLayout = JSON.parse(saved) as Array<{
        id: string;
        x: number;
        y: number;
        cols: number;
        rows: number;
      }>;
      const layout = savedLayout.find((l) => l.id === widget.id);
      if (layout) {
        widget.x = layout.x ?? widget.x;
        widget.y = layout.y ?? widget.y;
        widget.cols = layout.cols ?? widget.cols;
        widget.rows = layout.rows ?? widget.rows;
      }
    }

    const idx = this.widgets.findIndex((w) => w.id === widget.id);
    if (idx !== -1) {
      // Met à jour le widget existant
      this.widgets[idx] = widget;
    } else {
      this.widgets.push(widget);
    }
    // Force Angular à détecter le changement
    this.widgets = [...this.widgets];
  }

  // --- Layout ---
  saveLayout() {
    // Récupère le layout existant
    const saved = localStorage.getItem('dashboardWidgetsLayout');
    let layout: Array<{
      id: string;
      x: number;
      y: number;
      cols: number;
      rows: number;
    }> = [];
    if (saved) {
      layout = JSON.parse(saved);
    }

    // Mets à jour ou ajoute chaque widget
    this.widgets.forEach((w) => {
      const idx = layout.findIndex((l) => l.id === w.id);
      const widgetLayout = {
        id: w.id,
        x: w.x,
        y: w.y,
        cols: w.cols,
        rows: w.rows,
      };
      if (idx !== -1) {
        layout[idx] = widgetLayout;
      } else {
        layout.push(widgetLayout);
      }
    });

    localStorage.setItem('dashboardWidgetsLayout', JSON.stringify(layout));
  }

  createTasksPrioritiesWidget(
    widgetId: string,
    title: string,
    position: { x: number; y: number; cols: number; rows: number },
    filtre: any = {}
  ) {
    this.subscription.add(
      this.apiService.getAll('tasks/statistics', 1, 100, filtre).subscribe({
        next: (response: TasksPriorityListResponse) => {
          const barChartData = this.buildTasksPrioritiesBarChartData(response);
          this.addWidget({
            id: widgetId,
            x: position.x,
            y: position.y,
            cols: position.cols,
            rows: position.rows,
            component: BarChartComponent,
            data: {
              ...barChartData,
              title,
              filterConfig: {
                availableMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                selectedMonths: filtre.dueMonth,
                onMonthsChanged: (months: number[]) =>
                  this.onChangeTasksPrioritiesMonth(widgetId, months),
                filtre,
              },
              onBarClick: (event: any) => this.onBarClick(event),
            },
          });
        },
      })
    );
  }

  buildTasksPrioritiesBarChartData(
    response: TasksPriorityListResponse
  ): BarChartWidgetData {
    // 1. Récupère tous les types de priorité (labels) avec traduction
    const priorityLabels = response.member.map(
      (item: TaskPriorityTypeItem) => {
        const translationKey = `dashboard.priority.${item.priorityType}`;
        return this.translate.instant(translationKey) || item.priorityType;
      }
    );

    // 2. Récupère les valeurs (nombre de tâches par priorité)
    const values = response.member.map(
      (item: TaskPriorityTypeItem) => item.count
    );

    const colorMap: Record<string, string> = {
      NORMAL: '#C1C1C1',
      URGENT: '#F92E58',
      DONE: '#00CC75',
      IMPORTANT: '#FF9902',
    };
    const backgroundColors = response.member.map(
      (item: TaskPriorityTypeItem) => colorMap[item.priorityType] || '#0d6efd'
    );

    // 3. Crée le dataset (une seule série de données)
    const dataset = {
      label: 'Priorité',
      data: values,
      backgroundColor: backgroundColors, // couleur par défaut, à adapter si besoin
    };

    // 4. Ajoute les infos métiers dans extraInfo (indexé comme labels)
    const extraInfo: any[] = response.member.map(
      (item: TaskPriorityTypeItem) => {
        return {
          Priorité: [item],
          type: 'priority',
        };
      }
    );

    return {
      labels: priorityLabels,
      datasets: [dataset],
      extraInfo,
    };
  }

  onChangeDueDateMonth(widgetId: string, months: number[]) {
    // Retrouve le widget concerné
    const widget = this.widgets.find((w) => w.id === widgetId);
    if (widget) {
      // Mets à jour le filtre et relance la requête
      const filtre = { ...widget.data.filterConfig.filtre, dueMonth: months };
      this.createDueDateWidget(
        widgetId,
        widget.data.title,
        { x: widget.x, y: widget.y, cols: widget.cols, rows: widget.rows },
        filtre
      );
    }
  }

  onChangeDueDateCategory(widgetId: string, categoryCodes: string[]) {
    const widget = this.widgets.find((w) => w.id === widgetId);
    if (widget) {
      const filtre = {
        ...widget.data.filterConfig.filtre,
        'taskBatch.category.code': categoryCodes,
      };
      this.createDueDateWidget(
        widgetId,
        widget.data.title,
        { x: widget.x, y: widget.y, cols: widget.cols, rows: widget.rows },
        filtre
      );
    }
  }

  onChangeTasksPrioritiesMonth(widgetId: string, months: number[]) {
    // Retrouve le widget concerné
    const widget = this.widgets.find((w) => w.id === widgetId);
    if (widget) {
      // Mets à jour le filtre et relance la requête
      const filtre = { ...widget.data.filterConfig.filtre, dueMonth: months };
      this.createTasksPrioritiesWidget(
        widgetId,
        widget.data.title,
        { x: widget.x, y: widget.y, cols: widget.cols, rows: widget.rows },
        filtre
      );
    }
  }

  private extractId(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    if (typeof value === 'string') {
      return value.includes('/') ? extractLastSegmentFromUrl(value) : value;
    }
    if (typeof value === 'object') {
      const o = value as Record<string, unknown>;
      if (o['id'] != null) {
        const id = String(o['id']);
        return id.includes('/') ? extractLastSegmentFromUrl(id) : id;
      }
      if (o['@id'] != null) {
        return extractLastSegmentFromUrl(String(o['@id']));
      }
      if (o['user'] != null) {
        return this.extractId(o['user']);
      }
    }
    return String(value);
  }

  private extractTeamUserIds(team: any): string[] {
    const userIds = new Set<string>();
    const appendUserId = (value: any): void => {
      const userId = this.extractId(value);
      if (userId) {
        userIds.add(userId);
      }
    };

    appendUserId(team?.teamLeader);
    if (Array.isArray(team?.teamMembers)) {
      team.teamMembers.forEach((member: any) => appendUserId(member));
    }

    return Array.from(userIds);
  }

  private resolveTeamUserIds(
    teamValue: unknown,
    callback: (userIds: string[]) => void
  ): void {
    const teamId = this.extractId(teamValue);
    if (!teamId) {
      callback([]);
      return;
    }

    const cachedUserIds = this.teamUserIdsCache[teamId];
    if (cachedUserIds) {
      callback(cachedUserIds);
      return;
    }

    this.subscription.add(
      this.apiService.getById('teams', teamId).subscribe({
        next: (team: any) => {
          const userIds = this.extractTeamUserIds(team);
          this.teamUserIdsCache[teamId] = userIds;
          callback(userIds);
        },
        error: () => callback([]),
      })
    );
  }

  /**
   * Construit les filtres API à partir du formulaire (même logique `userRole[]` que my-folders).
   */
  private buildDashboardFilters(
    formValue: Record<string, unknown>,
    teamUserIds: string[] = []
  ): {
    filtre: Record<string, unknown>;
    companiesFiltre: Record<string, unknown>;
    plannedFiltre: Record<string, unknown>;
  } {
    const filtre: Record<string, unknown> = {};

    const uniqueRoleFilters: string[] = [];
    if (teamUserIds.length > 0) {
      uniqueRoleFilters.push(...teamUserIds);
    }

    const appendUniqueRoleFilters = (selectedValue: unknown, roleKey: string): void => {
      if (!selectedValue) return;
      const roleId = this.uniqueRoleIds[roleKey];
      const selectedUsers = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
      selectedUsers.forEach((selectedUser: unknown) => {
        const userId = this.extractId(selectedUser);
        if (userId && roleId) {
          uniqueRoleFilters.push(`${userId},${roleId}`);
        }
      });
    };

    appendUniqueRoleFilters(formValue['expertComptable'], 'expertComptable');
    appendUniqueRoleFilters(formValue['superviseur'], 'superviseur');
    appendUniqueRoleFilters(formValue['responsable'], 'responsable');

    if (formValue['user'] && formValue['role']) {
      const userIds = (Array.isArray(formValue['user']) ? formValue['user'] : [formValue['user']])
        .map((user: unknown) => this.extractId(user))
        .filter((id: string | null): id is string => Boolean(id));
      const roleIds = (Array.isArray(formValue['role']) ? formValue['role'] : [formValue['role']])
        .map((role: unknown) => this.extractId(role))
        .filter((id: string | null): id is string => Boolean(id));
      userIds.forEach((userId) => {
        roleIds.forEach((roleId) => uniqueRoleFilters.push(`${userId},${roleId}`));
      });
    } else if (formValue['user']) {
      const userIds = (Array.isArray(formValue['user']) ? formValue['user'] : [formValue['user']])
        .map((user: unknown) => this.extractId(user))
        .filter((id: string | null): id is string => Boolean(id));
      uniqueRoleFilters.push(...userIds);
    } else if (formValue['role']) {
      const roles = formValue['role'];
      if (Array.isArray(roles)) {
        roles.forEach((role: unknown) => {
          const roleId = this.extractId(role);
          if (roleId) {
            uniqueRoleFilters.push(`,${roleId}`);
          }
        });
      } else {
        const roleId = this.extractId(roles);
        if (roleId) {
          uniqueRoleFilters.push(`,${roleId}`);
        }
      }
    }

    if (uniqueRoleFilters.length > 0) {
      filtre['userRole'] = Array.from(new Set(uniqueRoleFilters));
    }

    if (formValue['assignedUser']) {
      const uid = this.extractId(formValue['assignedUser']);
      if (uid) {
        filtre['planneds.user'] = uid;
        filtre['planneds.role.code'] = 'EXECUTOR';
      }
    }

    if (formValue['year']) {
      filtre['dueYear'] = Number(formValue['year']);
    }

    if (formValue['site']) {
      const sites = Array.isArray(formValue['site']) ? formValue['site'] : [formValue['site']];
      if (sites.length > 0) {
        filtre['task.company.site'] = sites;
      }
    }

    const companiesFiltre: Record<string, unknown> = {};
    if (filtre['userRole']) {
      companiesFiltre['userRole'] = filtre['userRole'];
    }
    if (formValue['site']) {
      const sites = Array.isArray(formValue['site']) ? formValue['site'] : [formValue['site']];
      if (sites.length > 0) {
        companiesFiltre['site'] = sites;
      }
    }
    if (formValue['companyStatusCodes']) {
      const rawStatusCodes = Array.isArray(formValue['companyStatusCodes'])
        ? formValue['companyStatusCodes']
        : [formValue['companyStatusCodes']];

      const statusCodes = rawStatusCodes
        .map((status) => {
          if (typeof status === 'string') {
            return status;
          }
          if (status && typeof status === 'object' && 'code' in (status as Record<string, unknown>)) {
            return String((status as Record<string, unknown>)['code']);
          }
          return null;
        })
        .filter((code): code is string => !!code);

      if (statusCodes.length > 0) {
        companiesFiltre['companyStatus.businessCode'] = statusCodes;
      }
    }

    const plannedFiltre: Record<string, unknown> = { ...filtre };
    if (plannedFiltre['planneds.user']) {
      plannedFiltre['user'] = plannedFiltre['planneds.user'];
      delete plannedFiltre['planneds.user'];
    }
    if (plannedFiltre['planneds.role.code']) {
      plannedFiltre['role.code'] = plannedFiltre['planneds.role.code'];
      delete plannedFiltre['planneds.role.code'];
    }

    return { filtre, companiesFiltre, plannedFiltre };
  }

  onSearch(formValue: any): void {
    this.searchValue = formValue;
    const requestToken = ++this.searchRequestToken;
    this.resolveTeamUserIds(formValue?.team, (teamUserIds: string[]) => {
      if (requestToken !== this.searchRequestToken) {
        return;
      }
      const { filtre, companiesFiltre, plannedFiltre } =
        this.buildDashboardFilters(formValue || {}, teamUserIds);

      this.widgets = [];
      this.createCompaniesWidget(companiesFiltre);
      this.createTaskPlannedTabsWidget(plannedFiltre);
      this.createTaskWidgets(filtre);
      this.ensureCommentWidget();
    });
  }

  goToFolder(folderId: string): void {
    // Logique de redirection vers le détail du dossier
    if (folderId) {
      this.router.navigate(['my-folders/view-folder/', folderId]);
    }
  }

  // Méthode pour recharger les widgets
  private reloadWidgets(): void {
    const currentSearchValue = (this.searchValue || {}) as Record<string, unknown>;
    const requestToken = ++this.searchRequestToken;
    this.resolveTeamUserIds(currentSearchValue['team'], (teamUserIds: string[]) => {
      if (requestToken !== this.searchRequestToken) {
        return;
      }

      this.widgets = [];
      const { filtre, companiesFiltre, plannedFiltre } =
        this.buildDashboardFilters(currentSearchValue, teamUserIds);
      this.createCompaniesWidget(companiesFiltre);
      this.createTaskPlannedTabsWidget(plannedFiltre);
      this.createTaskWidgets(filtre);
      this.ensureCommentWidget();
    });
  }

  // Méthodes pour les actions des tâches
  canDeleteTask(task: any): boolean {
    return task && (task.type === 'TASK' || (task.type === 'WORKFLOW' && task.isMaster === true));
  }

  isTaskDisabled(task: any): boolean {
    return task && task.status && task.status.code === 'NOT_APPLICABLE';
  }

  // Méthode pour mettre à jour une tâche localement dans les widgets
  updateTaskInWidgets(updatedTask: any): void {
    // Mettre à jour dans tous les widgets qui contiennent des tâches
    this.widgets.forEach(widget => {
      if (widget.data?.tabs) {
        // Widget avec onglets (task-planned-tabs)
        widget.data.tabs.forEach((tab: any) => {
          if (tab.data) {
            const taskIndex = tab.data.findIndex((item: any) =>
              item.id === updatedTask.id || item.task?.id === updatedTask.id
            );
            if (taskIndex !== -1) {
              // Mettre à jour la tâche dans l'onglet
              if (tab.data[taskIndex].task) {
                tab.data[taskIndex].task = { ...tab.data[taskIndex].task, ...updatedTask };
              }
              // Recalculer les propriétés affichées
              const item = tab.data[taskIndex];
              item.isTaskDone = this.isTaskDone(updatedTask);
              item.user = this.getAssignedUser(updatedTask);
              item.company = updatedTask.company?.name || '';
              item.title = updatedTask.name || '';
              item.priority = this.getPriority(updatedTask);
              item.duration = this.getRemainingDuration(updatedTask);
              item.actualDuration = this.getActualDuration(updatedTask);
              item.taskType = this.getTaskType(updatedTask);
              item.dueDate = this.getDueDate(updatedTask);
              item.billing = this.getBillingType(updatedTask);
              item.amount = this.getAmount(updatedTask);
            }
          }
        });
      }
    });
  }

  // Méthodes utilitaires pour les propriétés des tâches (copiées de my-tasks)
  getAssignedUser(task: any): string {
    if (task.actors && task.actors.length > 0) {
      const executor = task.actors.find(
        (actor: any) => actor.role && actor.role.code === 'EXECUTOR'
      );

      if (executor && executor.user) {
        const fullName = `${executor.user.firstName || ''} ${executor.user.lastName || ''}`.trim();
        return fullName;
      }
    }
    return '';
  }

  getPriority(task: any): string {
    if (task.priority !== undefined && task.priority !== null) {
      return this.mapPriority(task.priority);
    }
    if (task.priorityType) {
      return this.mapPriorityType(task.priorityType);
    }
    return '';
  }

  mapPriority(priority: number): string {
    // Mapping : 0 → P3, 1 → P2, 2 → P1, 3 → P4, 4 → P5
    const priorityMap: { [key: number]: string } = {
      0: 'P3',
      1: 'P2',
      2: 'P1',
      3: 'P4',
      4: 'P5',
    };
    return priorityMap[priority] || priority.toString();
  }

  mapPriorityType(priorityType: string): string {
    const priorityMap: { [key: string]: number } = {
      'LOW': 0,
      'NORMAL': 1,
      'IMPORTANT': 1,
      'URGENT': 2
    };
    const numericPriority = priorityMap[priorityType];
    return numericPriority !== undefined ? this.mapPriority(numericPriority) : priorityType;
  }

  getRemainingDuration(task: any): string {
    const initialDuration = task.initialDuration;
    const computedDuration = task.computedDuration || 0;

    if (initialDuration !== null && initialDuration !== undefined && initialDuration >= 0) {
      const remainingDuration = initialDuration - computedDuration;

      if (remainingDuration <= 0) {
        return this.taskTimeDisplay.formatMinutes(0);
      }

      return this.taskTimeDisplay.formatMinutes(remainingDuration);
    }
    return '';
  }

  getActualDuration(task: any): string {
    const actualDuration = task.computedDuration;
    if (actualDuration !== null && actualDuration !== undefined && actualDuration >= 0) {
      return this.taskTimeDisplay.formatMinutes(actualDuration);
    }
    return '';
  }

  getTaskType(task: any): string {
    if (task.type) {
      return this.mapTaskType(task);
    }
    return '';
  }

  mapTaskType(task: any): string {
    const taskType = task.type;

    if (taskType === 'OBLIGATION') {
      return 'Échéance';
    }

    if (taskType === 'TASK') {
      return 'Tâche';
    }

    if (taskType === 'WORKFLOW') {
      if (task.parent && task.parent.type) {
        if (task.parent.type === 'OBLIGATION') {
          return 'Suivi d\'échéance';
        }
        if (task.parent.type === 'WORKFLOW') {
          return 'Suivi de tâche';
        }
      }
      return 'Suivi d\'échéance';
    }

    return taskType || '';
  }

  getDueDate(task: any): string {
    if (task.dueDate) {
      try {
        const date = new Date(task.dueDate);

        if (isNaN(date.getTime())) {
          return '';
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${day}/${month}`;
      } catch (error) {
        return '';
      }
    }
    return '';
  }

  getBillingType(task: any): string {
    if (task.billingType) {
      return this.mapBillingType(task.billingType);
    }
    return '';
  }

  mapBillingType(billingType: string): string {
    const billingTypeMap = {
      'BILLABLE': this.translate.instant('taskDescriptionModal.billingType.BILLABLE'),
      'PAID': this.translate.instant('taskDescriptionModal.billingType.PAID'),
      'INTERNAL': this.translate.instant('taskDescriptionModal.billingType.INTERNAL'),
      'FREE': this.translate.instant('taskDescriptionModal.billingType.FREE')
    };
    return billingTypeMap[billingType as keyof typeof billingTypeMap] || billingType;
  }

  getAmount(task: any): string {
    if (task.amount && task.amount > 0) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(task.amount);
    }
    return '';
  }

  isTaskDone(task: any): boolean {
    return task?.status?.code === 'DONE';
  }

  getTaskStatus(task: any): any {
    if (!task || !task.status) return null;

    const statusCode = task.status.code;
    return this.taskStatuses.find(s => s.code === statusCode) || null;
  }

  getTaskStatusName(task: any): string {
    const status = this.getTaskStatus(task);
    return status ? status.name : '';
  }

  getTaskStatusColor(task: any): string {
    const status = this.getTaskStatus(task);
    if (status && status.color) {
      return status.color;
    }
    // Couleur par défaut si pas de couleur définie
    return '#6c757d';
  }

  getTaskStatusTextColor(task: any): string {
    const bgColor = this.getTaskStatusColor(task);
    // Calculer la luminosité relative pour déterminer la couleur du texte
    const rgb = this.hexToRgb(bgColor);
    if (!rgb) return '#ffffff';

    // Calcul de la luminosité relative (formule WCAG)
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

    // Si la luminosité est supérieure à 0.5, utiliser du texte noir, sinon blanc
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    // Supprimer le # si présent
    const cleanHex = hex.replace('#', '');

    // Convertir en RGB
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return null;
    }

    return { r, g, b };
  }

  openTaskEditModal(task: any): void {
    if (!task) return;

    const modalRef = this.modalService.open(TaskEditModalComponent, {
      centered: true,
      size: 'lg',
    });
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.refreshWeeklyPlanning.subscribe((data: any) => {
      if (data.action === 'updateTask') {
        this.updateTaskInWidgets(data.updatedTask);
      } else {
        this.messageBus.setMessage({ type: 'dashboard:refresh', data: data });
        this.reloadWidgets();
      }
    });
  }

  openAssigneeModal(task: any): void {
    if (!task) return;

    const modalRef = this.modalService.open(TaskAssigneeModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.task = task;
    modalRef.componentInstance.refreshWeeklyPlanning.subscribe((data: any) => {
      if (data.action === 'updateTask' || data.action === 'changeAssignee') {
        this.updateTaskInWidgets(data.updatedTask);
      } else {
        this.messageBus.setMessage({ type: 'dashboard:refresh', data: data });
        this.reloadWidgets();
      }
    });
  }

  deleteTask(task: any): void {
    if (!task || !task.id) return;
    if (task.type !== 'TASK') {
      console.warn('Seules les tâches de type TASK peuvent être supprimées');
      return;
    }

    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: 'md',
    });

    modalRef.componentInstance.title = 'Confirmation de suppression';
    modalRef.componentInstance.message = 'Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible.';
    modalRef.componentInstance.confirmButtonText = 'Supprimer';
    modalRef.componentInstance.cancelButtonText = 'Annuler';

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.apiService.delete('tasks', task.id).subscribe({
          next: () => {
            // Supprimer la tâche des widgets sans recharger la page
            this.removeTaskFromWidgets(task.id);

            this.toastService.show({
              message: 'Tâche supprimée avec succès.',
              classname: 'bg-success text-light',
              delay: 4000,
            });
          },
          error: () => {
            this.toastService.show({
              message: 'Erreur lors de la suppression de la tâche.',
              classname: 'bg-danger text-light',
              delay: 5000,
            });
          }
        });
      }
    }).catch(() => {});
  }

  // Méthode pour supprimer une tâche des widgets
  removeTaskFromWidgets(taskId: string): void {
    this.widgets.forEach(widget => {
      if (widget.data?.tabs) {
        widget.data.tabs.forEach((tab: any) => {
          if (tab.data) {
            const taskIndex = tab.data.findIndex((item: any) =>
              item.id === taskId || item.task?.id === taskId
            );
            if (taskIndex !== -1) {
              tab.data.splice(taskIndex, 1);
            }
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // Ajoutez cette fonction utilitaire dans la classe DashboardComponent
private formatUserName(firstName: string, lastName: string): string {
  return formatUserName(firstName, lastName);
}



  /**
   * Méthode appelée lors du clic sur le graphique annuel (barres "Non fait")
   */
  onAnnualChartClick(data: any): void {
    // Rediriger vers la page échéances avec sélection automatique des statuts
    const queryParams: any = {
      autoSelectStatus: 'true',
      'task.dueYear': new Date().getFullYear().toString(),
    };

    // Si des données spécifiques sont disponibles, les ajouter
    if (data && data.month) {
      queryParams['task.dueMonth'] = data.month.toString();
    }

    this.router.navigate(['/deadline'], {
      queryParams: queryParams
    });
  }
}
